import React, { useEffect, useState } from 'react';
import{
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
}from 'recharts';
import { setI18n, useTranslation } from 'react-i18next';
import * as Constant from '../../../Constant';

import {OffLineJudgeGraphStyle} from './OffLineJudgeStyle';
import {
  GraphSetting,
  GraphDispSetting,
  GraphData,
  LineInfo,
  GraphPoint,
  OffLineJudgeComparisonData,
  OfflineJudgeDataType,
  SelectDataType,
  GetOffLineJudgeGraphLineData
} from './OffLineJudge';
import dayjs from 'dayjs';

import { GetMemoryScaleByNumber } from '../../../function/ChartCommon';
import {ToValueMaxRound, ToValueMinRound } from '../../../function/CalculationModel';

type OffLineJudgeChartProps = {
  line: SelectDataType;
  graphDispSetting: GraphDispSetting;
  isAutoRangeRef: boolean;
  OfflineJudgeList:OfflineJudgeDataType[]
  update: string;  
  onLoading: (isFlg: boolean) => void;
  onAutoRangeSet: (data: boolean) => void;
  onChagneYMinMax: (min: number, max: number) => void;
  onGraphClick: (xValue: number) => void;
};

/**
 * Y軸の最大値取得
 * @param a 
 * @param b 
 * @returns 
 */
const aryMax = function (a: number, b: number): number {
  return Math.max(a, b);
};
/**
 * Y軸の最小値取得
 * @param a 
 * @param b 
 * @returns 
 */
const aryMin = function (a: number, b: number): number {
  return Math.min(a, b);
};

/**
 * グラフスケールメモリ配列を返す
 * @param min
 * @param max
 * @returns
 */
const getTicks = (min: number, max: number): number[] => {
  if (min < 0 && max < 0) {
    return [min, max];
  }
  if (min > 0 && max > 0) {
    return [min, max];
  }
  return [min, 0, max];
};

/**
 * Y軸の値の最大値を取得
 * @param waveData
 * @returns
 */
const getLineInfoYMax = (waveData: GraphData): number => {
  let max = 0;

  if (waveData && waveData.points.length !== 0) {
    max = waveData.points.map((r) => r.yValue).reduce(aryMax);
  }
  if (max === 0) {
    return 1;
  } else {
    return max;
  }
  return 1;
};

/**
 * Y軸の値の最小値を取得
 * @param waveData
 * @returns
 */
const getLineInfoYMin = (waveData: GraphData): number => {
  let min = 0;

  if (waveData && waveData.points.length !== 0) {
    min = waveData.points.map((r) => r.yValue).reduce(aryMin);
  }
  if (min === 0) {
    return 0;
  } else {
    return min;
  }
  return 0;
};

/**
 * 二つの数字を受け取って、絶対値で比較して大きい値を返す関数
 * @param number1
 * @param number2
 * @returns
 */
const getMaxAbsoluteValue = (number1: number, number2: number): number => {
  const absolute1 = Math.abs(number1);
  const absolute2 = Math.abs(number2);

  if (absolute1 > absolute2) {
    return absolute1;
  } else if (absolute2 > absolute1) {
    return absolute2;
  } else {
    return absolute1;
  }
};

/**
 * グラフ描画用のポイントを生成
 */
const createChartPoints = (
  chartDt: LineInfo,
  pointCount: number,
  startIndex: number,
  xOffset: number,
  isDCRemoval: boolean
): Array<GraphPoint> => {
  console.debug(`chartDt=${chartDt} pointCount=${pointCount} startIndex=${startIndex} xOffset=${xOffset}`);
  var points = new Array<GraphPoint>();
  var sIdex = startIndex === -1 ? chartDt.XaxisStartIndex : startIndex;

  chartDt.XValues.filter((tmp) => tmp > sIdex + xOffset).forEach((item, index) => {
    if (pointCount > index) {
      points.push({
        xValue: (item - sIdex) * chartDt.SamplingRate,
        yValue: isDCRemoval ? chartDt.YValues[item] - chartDt.YaxisOffset : chartDt.YValues[item],
      });
    }
  });
  console.debug(['-OffLineJudgeChart createChartPoints- points', points]);
  return points;
};

const GetChartPointCount = (min: number, max: number, samplingRate: number): number => {
  console.debug(`(${max} - ${min}) / ${samplingRate}`);
  return (max - min) / samplingRate;
};

/**
 * グラフデータ更新処理
 * @param data 
 * @param xmin 
 * @param xmax 
 * @param isPileup 
 * @param isDCRemoval 
 * @returns 
 */
const UpDateGraphData = (
  data: OffLineJudgeComparisonData | undefined,
  xmin: number,
  xmax: number,
  isPileup: boolean,
  isDCRemoval: boolean
): GraphData[] => {
  console.debug(['-OffLineJudgeChart UpDateGraphData- isPileup,isDCRemoval', data, isPileup, isDCRemoval]);

  var ret: GraphData[] = [];

  if (data?.LineData != undefined) {
    var item: GraphData = {
      lineNo: 1,
      valueUpper: 0,
      valueUpperLimit: 0,
      selectFlg: true,
      points: [],
      infoColor: 'red',
      lineName: '1',
    };

    item.lineName = data.LineData.ItemName;
    var rowCount = GetChartPointCount(xmin, xmax, data.LineData.SamplingRate);
    var startIndex = data.LineData.XaxisStartIndex;
    var xoffset = xmin / data.LineData.SamplingRate;

    var points = createChartPoints(data.LineData, rowCount, startIndex, xoffset, isDCRemoval);
    item.points = points;
    ret.push(item);
    console.debug(['-OffLineJudgeChart GraphData- item', item]);
  }
  console.debug(['-OffLineJudgeChart GraphData- updateData', ret]);
  return ret;
};

const OffLineJudgeChart: React.FC<OffLineJudgeChartProps> = (props) => {
  const { t } = useTranslation();
  const [graphData, setGraphData] = useState<GraphData[]>([]);
  const [fetchData, setFetchData] = useState<OffLineJudgeComparisonData>();

  const [graphSetting, setGraphSetting] = useState<GraphSetting>({
    upper: 0,
    upperLimit: 0,
    yMax: 0,
    yMin: 0,
    xMax: 0,
    xMin: 0,
    backColor: Constant.GraphThemeLight.BACK_COLOR,
    memoryFontColor: Constant.GraphThemeLight.MEMORY_FONT_COLOR,
    yUnit: '',
    xUnit: '',
    yUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    xUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    gridColor: Constant.GraphThemeLight.GRID_COLOR,
  });
  
  /**
   * グラフのスタイル
   */
  const graphStyle = OffLineJudgeGraphStyle();

  /**
   * R相のグラフデータを絞り込み
   * @param OfflineJudgeList 
   * @param selectDt 
   * @returns 
   */
  const GetTargetData = (OfflineJudgeList: OfflineJudgeDataType[], selectDt: SelectDataType): OfflineJudgeDataType | undefined => {
    console.debug(['GetTargetData-OfflineJudgeList, selectDt', OfflineJudgeList, selectDt]);
    var OfflineJudgeRow = OfflineJudgeList.find((item) => item.DEV_INDEX == Number(selectDt.devIndex) && item.SPEED == 2 && item.ITM_NAME_NO == 3);  //計測種別R相固定

    if (OfflineJudgeRow) {
      return OfflineJudgeRow;
    }
    return undefined;
  };

  /**
   * 区間選定ボタン押下時
   */
  useEffect(() => {
    console.debug(['OfflineJudgeList,lineSetting', props.OfflineJudgeList, props.line]);
    props.onLoading(true);
    var target = GetTargetData(props.OfflineJudgeList, props.line);
    var day = dayjs(props.line.selectDate).format('YYYY-MM-DD');
    var time = props.line.selectTime;

    GetOffLineJudgeGraphLineData(
      Number(target?.PLC_NO),
      Number(target?.AI_CARD),
      Number(target?.AI_CH),
      `${day} ${time}`
    ).then((ret) => {
      setFetchData(ret);
      props.onLoading(false);
    });
  }, [props.update]);

  /**
   * グラフデータ取得後処理
  */
  useEffect(() => {
    if (!fetchData) return;

    var gdata = UpDateGraphData(
      fetchData,
      props.graphDispSetting.xMin,
      props.graphDispSetting.xMax,
      props.graphDispSetting.isPileup,
      props.graphDispSetting.isDCRemoval
    );
    setGraphData(gdata);
    const unit = fetchData.LineData?.UnitSymbol ?? '';
    
    if (props.isAutoRangeRef) {

      //console.debug("-OffLineJudgeChart isAutoRange");
      var yMax = getLineInfoYMax(gdata[0]);
      var yMin = getLineInfoYMin(gdata[0]);
      //console.debug(["-OffLineJudgeChart yMax,yMin", yMax, yMin]);
    
      //小数点切り捨て
      yMax = Math.round(yMax * 100) / 100;
      yMin = Math.round(yMin * 100) / 100;
      //console.debug(["-OffLineJudgeChart yMax,yMin,, yMax, yMin]);
      yMax = ToValueMaxRound(yMax);
      yMin = ToValueMinRound(yMin);
      //console.debug(["-OffLineJudgeChart yMax,yMin", yMax, yMin,]);
      const y1 = getMaxAbsoluteValue(yMax, yMin);
      yMax = y1;
      yMin = -y1;
    
      setGraphSetting(prev => ({
        ...prev,
        yMax: yMax,
        yMin: yMin,
        yUnit: unit,
        xUnit: 'msec',
      }));
      console.debug(['fetchData yMax, yMin', fetchData, yMax, yMin]);
      props.onAutoRangeSet(false);
      props.onChagneYMinMax(yMin, yMax);
    } else {
      setGraphSetting(prev => ({
        ...prev,
        yUnit: unit,
        xUnit: 'msec',
      }));
    }
  }, [fetchData]);

  /**
   * グラフデータ取得後の処理(X軸)
   */
  useEffect(() => {
    if (!fetchData) return;

    // x軸が変わった時だけデータを作り直す
    const gdata = UpDateGraphData(
      fetchData,
      props.graphDispSetting.xMin,
      props.graphDispSetting.xMax,
      props.graphDispSetting.isPileup,
      props.graphDispSetting.isDCRemoval
    );
    setGraphData(gdata);
  }, [
    props.graphDispSetting.xMin,
    props.graphDispSetting.xMax,
  ]);

  /**
  * グラフデータ取得後の処理(Y軸)
  */
  useEffect(() => {
    setGraphSetting(prev => ({
      ...prev,
      yMin: props.graphDispSetting.yMin,
      yMax: props.graphDispSetting.yMax,
    }));
  }, [
    props.graphDispSetting.yMin,
    props.graphDispSetting.yMax,
  ]);

  /**
   * カスタムツールチップ
   * @param param0 
   * @returns 
   */
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active) {
      return (
        <div className={graphStyle.customTooltip}>
          <div className="label">{`${t('OffLineJudgeChart.時間', '時間')} : ${Number(label).toFixed(3)}`}</div>
          {payload?.map((r: any, index: any) => {
            if (index <= 2 && r.name !== 'yValue') {
              return (
                <div style={{ color: r.color }}>
                  {r.name} : {Number(r.value).toFixed(3)}
                </div>
              );
            }
          })}
        </div>
      );
    }
    return null;
  };

return(
  <ResponsiveContainer className={graphStyle.responsiveContainer} width="99%">
    <LineChart
      style={{ background: graphSetting.backColor }}
      width={500}
      height={300}
      margin={{ top: 10, right: 50, left: 1, bottom: 0 }}
      onClick={(e: any) => {
        if (e && e.activeLabel !== undefined) {
        props.onGraphClick(e.activeLabel);
      }
  }}
     >
      <CartesianGrid stroke={graphSetting.gridColor} strokeDasharray="3 3" />
      {/**X軸 */}
      <XAxis
        dataKey="xValue"
        type="number"
        allowDuplicatedCategory={false}
        stroke={graphSetting.memoryFontColor}
        domain={[props.graphDispSetting.xMin, props.graphDispSetting.xMax]}
        height={50}
        ticks={GetMemoryScaleByNumber(props.graphDispSetting.xMin, props.graphDispSetting.xMax, 10)}
        tick={{ dy: 10 }}
        padding={{ right: 0, left: 0 }}
      >
        <Label angle={0} position="insideBottomRight" style={{ textAnchor: 'middle', padding: 0 }}>
          msec
        </Label>
      </XAxis>
      {/**y軸 */}
      <YAxis
        yAxisId={1}
        allowDataOverflow={true}
        dataKey="yValue"
        type="number"
        stroke={graphSetting.memoryFontColor}
        tickFormatter={(props) => props.toFixed(2)}
        ticks={getTicks(graphSetting.yMin, graphSetting.yMax)}
        domain={[graphSetting.yMin, graphSetting.yMax]}
        width={95}
      >
        <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle', padding: 0 }}>
          {graphSetting.yUnit}
        </Label>
      </YAxis>
      <Tooltip content={<CustomTooltip />} />
      {/**グラフライン表示 */}
      <Line
        yAxisId={1}
        connectNulls
        dataKey="yValue"
        stroke={graphData[0]?.infoColor}
        data={graphData[0]?.points}
        strokeWidth={2}
        dot={false}
        name={graphData[0]?.lineName}
        hide={false}
        isAnimationActive={false}
      />
      </LineChart>   
    </ResponsiveContainer>
)};

export default OffLineJudgeChart;